import { FC, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@altermaind/web/ui';
import { DEFAULT_LANGUAGE, supportedLanguages } from '@/_shared/constants';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { SupportedLocale } from '@/_shared/types/languages';
import { useLanguageSelector } from './useLanguageSelector';

const LANG_STORAGE_KEY = 'app_lang';

const getSavedLanguage = () => {
  const savedLang = localStorage.getItem(LANG_STORAGE_KEY);
  return savedLang || DEFAULT_LANGUAGE;
};

export const LanguageSelector: FC = () => {
  const { isMobile } = useBreakingPoints();
  const { changeLanguage } = useLanguageSelector();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLocale>(
    () =>
      supportedLanguages.find(locale => locale.value === getSavedLanguage()) ??
      supportedLanguages[0],
  );

  const handleSelect = (language: SupportedLocale) => {
    if (language.value !== selectedLanguage.value) {
      setSelectedLanguage(language);
      changeLanguage(language.value, language.langCode);
    }
    setIsOpen(false);
  };

  // Close the side panel when clicking outside the selector
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <Box ref={containerRef} position='relative' width='100%' mb='-xxs'>
      <Box
        data-testid='language-selector'
        display='flex'
        flexDirection='row'
        alignItems='center'
        width='100%'
        p='xxs'
        borderRadius='m'
        backgroundColor={isHovered ? 'grey/dark' : undefined}
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <Typography variant='p2/light' fontSize='xs'>
          {selectedLanguage.label}
        </Typography>
      </Box>
      {isOpen && (
        <Box
          data-testid='language-selector-panel'
          position='absolute'
          // Desktop: open as a side panel to the left of the profile card.
          // Mobile: the card is full-width, so fall back to opening below.
          top={isMobile ? 'calc(100% + 8px)' : '0'}
          right={isMobile ? '0' : 'calc(100% + 8px)'}
          minWidth={180}
          bg='grey/dark/background'
          p='s'
          borderRadius='l'
          boxShadow='0px 0px 16px 0px #00000066'
          zIndex='999999'
          display='flex'
          flexDirection='column'
        >
          {supportedLanguages.map(language => (
            <Box
              key={language.value}
              data-testid='language-selector-option'
              p='xxs'
              borderRadius='m'
              display='flex'
              alignItems='center'
              style={{ cursor: 'pointer' }}
              backgroundColor={language.value === selectedLanguage.value ? 'grey/dark' : undefined}
              onClick={() => handleSelect(language)}
            >
              <Typography variant='p2/light' fontSize='xs'>
                {language.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
